import { PremiumGate } from '../../../platform/access/PremiumGate'
import { Skeleton } from '../../../platform/async/Skeleton'
import type { AsyncState } from '../../../platform/async/Async'
import type { LocalDate } from '../../../platform/localDate'
import { useNavigate } from 'react-router'
import { writeView } from '../../cashflow/cashflowPeriod'
import { ChartPreview } from '../../cashflow/LockedPreview'
import { OverviewWidget } from '../OverviewWidget'
import { CashflowLines } from './CashflowLines'
import type { ChartView } from './chartView'

/**
 * Your cashflow this year: two smooth net lines, this year solid and last year dashed, as
 * the design draws it. Every point is a server bucket's `net`. The widget's ↗ opens `/cashflow`; choosing a month in the pills only selects it, and the link beside the chip opens Cashflow at that month.
 * A free user gets the locked chart and never the data.
 */
export function CashflowWidget({ state, entitled, today }: { state: AsyncState<ChartView>; entitled: boolean; today: LocalDate }) {
  const navigate = useNavigate()
  const year = Number(today.slice(0, 4))
  const common = { title: 'Your cashflow this year', subtitle: `Solid = ${year} · dashed = ${year - 1}`, href: '/cashflow' } as const

  if (!entitled) {
    return (
      <OverviewWidget<null> {...common} surface="overview-cashflow" state={{ status: 'success', data: null }} skeleton={null} empty={{ title: '' }}>
        {() => (
          <PremiumGate entitlement="cashflow_details" cta="none" panelKind="chart" preview={<ChartPreview />}>
            {null}
          </PremiumGate>
        )}
      </OverviewWidget>
    )
  }

  return (
    <OverviewWidget<ChartView>
      {...common}
      surface="overview-cashflow"
      state={state}
      skeleton={<Skeleton height={160} />}
      empty={{ title: 'No history yet', action: { label: 'See accounts', onAct: () => void navigate('/accounts') } }}
    >
      {(view, headingId) => (
        <CashflowLines
          view={view}
          today={today}
          labelledBy={headingId}
          hrefFor={(m) => {
            const period = `${view.year}-${String(m + 1).padStart(2, '0')}-01`
            const query = writeView({ granularity: 'month', period }, today).toString()
            return query === '' ? '/cashflow' : `/cashflow?${query}`
          }}
        />
      )}
    </OverviewWidget>
  )
}
